import { apiFetch } from "../api/client";
import { assertSession, SessionChangedError } from "../auth/session";
import type { Answers } from "./answers";
import {
  saveSelection,
  cleanSelection,
  sameSelection,
  type Article,
  type Selection,
} from "./articles";
import { selectedDaysOf } from "./dates";
import { registrationChannel } from "./registrationChannel";

/**
 * THE PARTICIPANT, AND THEIR PARTICIPATION IN THIS EVENT.
 *
 * Three routes of api-v1, read from the product rather than guessed:
 *
 *   GET  /domains/:d/users/me                     → who is signed in (`id`)
 *   GET  /domains/:d/events/:e/users/:id          → the user, with `UserEvent`
 *   PUT  /domains/:d/events/:e/users/:id          → writes `UserEvent` fields, AND
 *                                                   answers what the GET answers
 *
 * The identity comes in TWO steps because a token says who you are on a DOMAIN,
 * not what you are in an event. Nothing in the token carries the event-scoped id
 * the write needs.
 *
 * ⚠️ THE COLUMN IS `isRegistered`. V1 reads `UserEvent.registered` — a field that
 * does not exist in the payload — so it never restores a previous "on site" or
 * "remote" answer: the step reopens blank and the participant answers again. Read
 * against the real response, not against V1's reading of it.
 */

export type Recorded = {
  /**
   * ═══ QUELLE PARTICIPATION ═══ `UserEvents.id`, absent quand il n'y en a aucune.
   *
   * ⚠️ « AUCUNE » N'EST PAS « PAS RÉPONDU ». La route rend `UserEvent: null` quand la ligne
   * n'existe pas — supprimée depuis la fiche contact du BO, par exemple — et c'est ce qui
   * distingue une inscription effacée d'une invitation sans réponse. L'empreinte des réponses
   * du tunnel s'en sert (voir `answerSession.ts`).
   */
  id?: number;
  /**
   * ═══ A-T-ON RÉPONDU ? ═══ Le champ que le back-office nomme « Réponse ».
   *
   * ⚠️ C'EST LUI QUI COMMANDE LA NAVIGATION, ET LUI SEUL. Faux : on est renvoyé au début
   * et on avance d'une étape à la fois. Vrai : tout le parcours est ouvert, on revient
   * corriger ce qu'on veut. Il n'y a pas de troisième source de vérité — l'état de
   * navigation ne mémorise plus quelles étapes ont été franchies, parce qu'il survivait
   * aux rechargements et faisait passer pour répondu quelqu'un qui n'avait fait que
   * traverser.
   */
  responded: boolean;
  /**
   * ⚠️ `isCompleted` N'EST PAS LU ICI, ET SON ABSENCE EST VOULUE. La route le rend ; ce
   * type ne le reprend pas, pour qu'aucun écran ne puisse s'y raccrocher par mégarde.
   * Ce que la confirmation veut savoir — « reste-t-il quelque chose à régler ? » — se
   * demande au SOLDE, qui est calculé à la demande et ne peut pas dériver. Voir la note
   * de `saveParticipation` pour ce que cette colonne vaut réellement.
   */
  /** What they answered last time, if they have. */
  answer?: "onsite" | "remote" | "represented" | "decline";
  /** Why they are not coming — the `message` column. */
  reason?: string;
  /**
   * ═══ THE BADGE CODE ═══ `UserEvents.code`, THIS registration's identifier.
   *
   * ⚠️ IT IS WHAT THE QR ENCODES, and nothing else: the badge the server prints uses it
   * as is (`badgeTemplates/render` → `qr: r.code`), V1 does the same on screen, and the
   * entrance scan therefore reads that code. A screen encoding the user id would produce
   * a QR that scans and matches nobody.
   *
   * ⚠️ NO EXTRA REQUEST: the route already returns the whole registration row. Absent
   * while the registration does not exist — neither does the badge.
   */
  code?: string;
  /** The profile recorded for them. */
  typoId?: number | null;
  /**
   * LA SOCIÉTÉ POUR LAQUELLE ILS VIENNENT — leur rattachement (`Employees.id`), pas
   * l'identifiant de la société.
   *
   * ⚠️ ET C'EST BIEN SUR LA PARTICIPATION QU'IL VIT, PAS SUR LA PERSONNE. Quelqu'un
   * peut être employé de deux sociétés du domaine ; celle qu'il représente à CET
   * événement-ci est celle que cette colonne désigne. Voir `funnel/companies.ts`.
   */
  employeeId?: number;
  /**
   * LES JOURS DÉJÀ RETENUS — des `EventDays.id`.
   *
   * ⚠️ AUCUNE REQUÊTE DE PLUS : la route rend déjà `UserEvent.UserEventDays`
   * (`UserEventService.getByUserId` les inclut). C'est ce qui permet au tunnel de
   * rouvrir l'étape sur ce qui a été coché la fois d'avant, et à la confirmation de ne
   * pas réinscrire tous les jours par-dessus un choix existant.
   *
   * ⚠️ ET C'EST AUSSI CE QUI DIT CE QU'IL N'Y A PAS À ÉCRIRE : `saveParticipation` n'envoie
   * un jour que s'il change d'état. Absent, on ne sait pas, et l'écriture les envoie tous.
   */
  days?: number[];
  /** Kept whole so a write never drops what another screen put there. */
  extended: Record<string, unknown>;
};

/** La réponse dit qu'on vient : sur place ou à distance. */
export const comes = (answer: string | undefined): boolean =>
  answer === "onsite" || answer === "remote";

/**
 * ═══ UNE INSCRIPTION EXISTE — ET NON « A RÉPONDU » (bug #522) ═══
 *
 * ⚠️ UN REFUS POSE AUSSI `responded: true`. Lu comme « inscrit », il ouvrait à quelqu'un
 * qui ne vient pas les pages de correction, le bouton « Enregistrer » et tout le fil :
 * repasser de « je ne viens pas » à « à distance » l'inscrivait d'un clic, sans profil,
 * sans formulaire, sans société, et à TOUS les jours (le serveur n'en garde aucun après
 * un refus, `selectedDaysOf` retombe alors sur tous). Corriger suppose une inscription ;
 * venir après avoir refusé, c'est en faire une, donc refaire le parcours.
 */
export function isRegistered(
  recorded: Pick<Recorded, "responded" | "answer"> | undefined,
): boolean {
  return recorded?.responded === true && comes(recorded.answer);
}

type UserEvent = {
  id?: number | null;
  responded?: boolean;
  isRegistered?: boolean;
  isRemote?: boolean;
  representedBy?: number | null;
  message?: string | null;
  code?: string | null;
  typoId?: number | null;
  employeeId?: number | null;
  UserEventDays?: ReadonlyArray<{
    eventDayId?: number | null;
    registered?: boolean | null;
  }> | null;
  extended?: Record<string, unknown> | null;
};

/** Who is signed in — the identity the recap names and the write needs. */
export type Me = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  /**
   * LA LANGUE DE CETTE PERSONNE — `Users.languageId`, et `undefined` tant qu'elle n'en a
   * jamais choisi une.
   *
   * ⚠️ ELLE VIT SUR LE COMPTE, PAS DANS CE NAVIGATEUR, et c'est ce qui la rend utile : on
   * la retrouve depuis un téléphone, l'année suivante, et le back office s'en sert pour
   * les envois. Voir `settings/langue.ts`.
   */
  languageId?: number;
  /**
   * SON ADRESSE POSTALE — les colonnes de `Users`, sous leurs noms, absentes quand elles
   * sont vides. Saisie au back office ou par un import, elle pré-remplit la facturation
   * d'une première facture (voir `knownBill`, bug #358).
   */
  address1?: string;
  address2?: string;
  postalCode?: string;
  city?: string;
};

/** Les colonnes d'adresse de `Users` que `Me` reprend. */
const ADDRESS = ["address1", "address2", "postalCode", "city"] as const;

export async function fetchMe(
  domainId: number,
  signal?: AbortSignal,
): Promise<Me> {
  const me = await apiFetch<{
    id?: number;
    firstName?: string | null;
    lastName?: string | null;
    languageId?: number | null;
    Authentication?: { email?: string | null } | null;
  } & Partial<Record<(typeof ADDRESS)[number], string | null>>>(
    `/domains/${domainId}/users/me`,
    { signal },
  );

  if (typeof me?.id !== "number") {
    throw new Error("Le compte connecté n'a pas pu être identifié.");
  }
  return {
    id: me.id,
    firstName: me.firstName ?? "",
    lastName: me.lastName ?? "",
    // The address lives on the AUTHENTICATION, not on the user: one account can
    // hold several users, one per domain.
    email: me.Authentication?.email ?? "",
    ...(typeof me.languageId === "number" ? { languageId: me.languageId } : {}),
    ...Object.fromEntries(
      ADDRESS.flatMap((key) => {
        const value = me[key];
        return typeof value === "string" && value.trim() ? [[key, value.trim()]] : [];
      }),
    ),
  };
}

/**
 * ÉCRIT LA LANGUE SUR LE COMPTE — `PUT /domains/:d/users/me`.
 *
 * ⚠️ SUR `me`, ET SURTOUT PAS SUR UN IDENTIFIANT QU'ON ENVOIE. La route résout la personne
 * depuis le JETON quand l'adresse n'en nomme aucune (`user.controller.js`, `updateOne`) :
 * personne ne peut donc écrire chez quelqu'un d'autre, même en composant la requête à la
 * main. La V1, elle, passe par la route d'apix qui prend un identifiant en paramètre.
 *
 * ⚠️ ET SON ÉCHEC NE SE VOIT PAS À L'ÉCRAN, délibérément : la langue est DÉJÀ changée
 * localement quand cet appel part. Un message d'erreur pour une préférence d'affichage qui
 * fonctionne sous les yeux de la personne ne lui apprendrait rien d'utile.
 */
export async function saveLanguage(
  domainId: number,
  languageId: number,
): Promise<void> {
  await apiFetch(`/domains/${domainId}/users/me`, {
    method: "PUT",
    body: { languageId },
  });
}

/** Le remplaçant désigné, rangé dans `extended` faute de pouvoir écrire `representedBy`. */
const STAND_IN = "REPRESENTED_BY_DETAILS";

/** Qui vient à la place — prénom, nom, adresse. */
export type StandIn = { first: string; last: string; email: string };

/**
 * LE REMPLAÇANT DÉJÀ ENREGISTRÉ, LU DANS LE SAC `extended`.
 *
 * ⚠️ UN SEUL ENDROIT QUI SAIT OÙ IL DORT. La clé était écrite en dur à l'écriture, et
 * nulle part en lecture : trois écrans (l'étape 1, le récapitulatif, la relecture de la
 * réponse) l'ignoraient donc, chacun pour son compte. `extended` vient du serveur : rien
 * ne garantit sa forme, d'où la lecture défensive.
 *
 * Vide de part en part = pas de remplaçant : trois chaînes vides ne désignent personne, et
 * les afficher ferait croire à une désignation.
 */
export function standOf(
  extended: Record<string, unknown> | null | undefined,
): StandIn | undefined {
  const bag = extended?.[STAND_IN];
  if (!bag || typeof bag !== "object") return undefined;
  const raw = bag as Record<string, unknown>;
  const text = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const stand = {
    first: text(raw.first),
    last: text(raw.last),
    email: text(raw.email),
  };
  return stand.first || stand.last || stand.email ? stand : undefined;
}

/**
 * CE QUE CETTE PERSONNE A RÉPONDU — `undefined` tant qu'elle n'a rien répondu.
 *
 * ⚠️ C'EST `responded` QUI DIT SI L'ON A RÉPONDU, PAS `isRegistered`. Le back-office en
 * fait deux onglets distincts — « Réponse » sur le premier, « Participation » sur le
 * second (`bo/src/pages/user/Event/index.jsx`) — et la documentation du produit est
 * explicite : en lecture, le mode « ne rend que `null` (pas encore répondu), `onsite` ou
 * `remote` », et « un refus se lit `{ responded: true, registered: false }` ».
 *
 * ⚠️ LA NUANCE N'EST PAS THÉORIQUE : `isRegistered` est RECALCULÉ par le serveur à partir
 * des jours retenus, donc il devient vrai dès qu'on inscrit quelqu'un depuis le back
 * office ou par un import — sans que l'intéressé ait rien dit. Lu comme une réponse, il
 * faisait circuler librement dans le tunnel des gens que le back office affiche « 🔴
 * Réponse », et personne ne leur demandait jamais de confirmer ce qu'on avait saisi pour
 * eux. Ils repassent désormais par le parcours, où leurs réponses déjà enregistrées
 * s'affichent pré-remplies ; en confirmant, `responded` passe à vrai et les deux écrans
 * disent enfin la même chose.
 *
 * ⚠️ ET LA RÉPONSE SE DÉDUIT ENSUITE, jamais avant : une fois `responded` acquis,
 * `isRegistered` et `isRemote` disent LAQUELLE. Un refus, c'est avoir répondu sans venir
 * — il n'existe aucune valeur « declined » à lire, la documentation le souligne.
 */
function answerOf(ue: UserEvent): Recorded["answer"] | undefined {
  if (!ue.responded) return undefined;
  if (ue.isRegistered && ue.isRemote) return "remote";
  if (ue.isRegistered) return "onsite";
  /**
   * ⚠️ ET LE REMPLAÇANT SE LIT LÀ OÙ L'ÉCRITURE LE RANGE. Cette relecture ne regardait
   * que `representedBy`, une colonne que le FO N'ÉCRIT JAMAIS (voir `saveParticipation` :
   * c'est une clé étrangère vers un compte, donc la désignation part dans `extended`).
   * « Je serai représenté » se relisait donc « je ne participe pas » au retour : le nom du
   * remplaçant disparaissait de l'écran, et le premier « Enregistrer » le supprimait pour
   * de bon, puisque la réponse relue n'était plus « represented ».
   */
  if (ue.representedBy || standOf(ue.extended)) return "represented";
  return "decline";
}

/**
 * LA PARTICIPATION, LUE DANS CE QUE LA ROUTE REND.
 *
 * ⚠️ UNE SEULE LECTURE POUR LA QUESTION ET POUR LA RÉPONSE, et ce n'est pas un
 * rangement : c'est ce qui permet à l'écriture de RENDRE ce qu'elle vient d'écrire au
 * lieu de le faire relire. Le `PUT` répond exactement ce que le `GET` répond — les deux
 * terminent sur `UserEventService.getByUserId`, jours et code compris. Voir la fin de
 * `saveParticipation`, et `useParticipation`, qui en garnit son cache.
 */
function recordedOf(ue: UserEvent): Recorded {
  return {
    ...(typeof ue.id === "number" ? { id: ue.id } : {}),
    responded: ue.responded === true,
    ...(answerOf(ue) ? { answer: answerOf(ue) } : {}),
    ...(typeof ue.message === "string" && ue.message
      ? { reason: ue.message }
      : {}),
    // Empty means absent: a blank code encoded in a QR would give a valid square that
    // the scanner would not recognise.
    ...(typeof ue.code === "string" && ue.code.trim()
      ? { code: ue.code.trim() }
      : {}),
    ...(typeof ue.typoId === "number" ? { typoId: ue.typoId } : {}),
    ...(typeof ue.employeeId === "number"
      ? { employeeId: ue.employeeId }
      : {}),
    // Seulement les jours RETENUS : la table porte aussi une ligne par jour refusé,
    // et les compter reviendrait à dire « inscrit partout » de quelqu'un qui a choisi.
    ...(Array.isArray(ue.UserEventDays)
      ? {
          days: ue.UserEventDays.flatMap((d) =>
            d?.registered && typeof d.eventDayId === "number"
              ? [d.eventDayId]
              : [],
          ),
        }
      : {}),
    extended: (ue.extended ?? {}) as Record<string, unknown>,
  };
}

export async function fetchParticipation(
  domainId: number,
  eventId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<Recorded> {
  const user = await apiFetch<{ UserEvent?: UserEvent } | null>(
    `/domains/${domainId}/events/${eventId}/users/${userId}`,
    { signal },
  );
  return recordedOf(user?.UserEvent ?? {});
}

/**
 * LES JOURS QUE L'ÉCRITURE RETIENDRA, et donc ceux dont les billets tomberont. `null`
 * quand l'événement ne déclare aucun jour : il n'y a alors rien à écrire ni à filtrer.
 *
 * ⚠️ UNE SEULE RÈGLE POUR L'ÉCRITURE ET POUR CE QU'ON EN ANNONCE. `stepSave` prévient
 * avant de retirer un billet réglé (tâche #212) : jugé sur une seconde lecture des mêmes
 * champs, l'avertissement pourrait se taire sur un billet que l'écriture retire.
 */
export function daysToWrite(
  answers: Answers,
  recorded: Pick<Recorded, "answer" | "days">,
  allDays: ReadonlyArray<number>,
): number[] | null {
  if (allDays.length === 0) return null;
  const answer = answers.participation ?? recorded.answer;
  return selectedDaysOf({
    coming: answer === "onsite" || answer === "remote",
    ...(answers.days ? { chosen: answers.days } : {}),
    ...(recorded.days ? { saved: recorded.days } : {}),
    all: allDays,
  });
}

/**
 * WRITES THE ANSWERS, in the columns the product already reads.
 *
 * ⚠️ ET DANS UNE SECONDE TABLE : `UserEventDays`, une ligne par jour de l'événement.
 * Ce n'est pas réservé aux événements de plusieurs jours — c'est justement là que la
 * V2 mentait le plus discrètement. Elle n'écrivait aucun jour, or « inscrit » VEUT
 * DIRE chez le produit « inscrit à au moins un jour » : ses participants portaient
 * donc `isRegistered: true` et ressortaient « non inscrits » de tout ce qui recompte,
 * badge et contrôle d'accès compris. Sans étape dates traversée, tous les jours sont
 * retenus — voir `funnel/dates.ts`.
 *
 * ⚠️ `extended` IS MERGED, never replaced. It is a shared bag — the waiting list,
 * other screens' scraps — and sending a fresh object would silently delete
 * whatever else lives there.
 *
 * ⚠️ THE STAND-IN GOES INTO `extended`, not into `representedBy`. That column holds
 * a USER id, and creating that user is its own route (`POST …/users/create-invite`)
 * with its own consequences — it sends them the badge. Writing a name into a
 * foreign key would either fail or point at somebody else's account, so the
 * designation is recorded where it can do no harm until that route is wired.
 */
async function writeParticipation(
  domainId: number,
  eventId: number,
  userId: number,
  answers: Answers,
  recorded: Recorded,
  /**
   * Tous les jours de l'événement. Vide — ou absent — il n'y a rien à écrire côté
   * jours, et la participation part seule, comme avant l'étape dates.
   */
  allDays: ReadonlyArray<number> = [],
  /**
   * ═══ CE QUE LE NAVIGATEUR SAIT DÉJÀ ═══ deux indices, tous deux facultatifs, et
   * AUCUNE requête de plus : ils sont lus dans le cache des écrans traversés.
   *
   * Ils ne changent pas ce qui est écrit — ils évitent des écritures qui n'écriraient
   * rien. C'est la confirmation qui les reçoit, et c'est elle qui attend : chaque appel
   * épargné est un aller-retour de moins derrière son « Enregistrement… ».
   */
  cached: {
    /**
     * Le catalogue des articles. Sert à écarter un identifiant périmé avant l'écriture :
     * la route détruit la commande avant de la recréer, et un id introuvable la ferait
     * échouer. Absent, seul le filtre par jour agit.
     */
    known?: ReadonlyArray<Article>;
    /**
     * La commande DÉJÀ ENREGISTRÉE. Identique à celle qu'on s'apprête à écrire, il n'y a
     * rien à écrire — voir `sameSelection`. Absente, on écrit, comme avant.
     */
    order?: Selection;
    /**
     * L'empreinte de la session sous laquelle l'écran a été composé (`auth/session.ts`).
     * Vérifiée avant CHAQUE écriture : un autre onglet qui change de compte entre deux
     * appels ne doit pas voir la suite partir sous l'identité de l'autre.
     */
    session?: string;
  } = {},
  progress: WriteProgress = { days: false, tickets: false },
): Promise<Recorded | undefined> {
  const still = () => {
    if (cached.session !== undefined) assertSession(cached.session);
  };
  /**
   * ⚠️ CE QUI EST RÉPONDU MAINTENANT, SINON CE QUE LE SERVEUR DÉTIENT DÉJÀ — et cette
   * seconde source n'est pas du confort, elle empêche une DÉSINSCRIPTION accidentelle.
   *
   * Quelqu'un qui est déjà inscrit et revient par le fil d'Ariane ajouter une option ne
   * repasse pas par l'étape 1 : son état de navigation ne porte donc aucune réponse de
   * participation. Lue seule, elle valait `undefined`, donc « ne vient pas » — et
   * l'écriture qui suit aurait posé `isRegistered: false` et retiré TOUS ses jours. Il
   * serait reparti non inscrit pour avoir voulu ajouter un déjeuner.
   */
  const answer = answers.participation ?? recorded.answer;
  const coming = answer === "onsite" || answer === "remote";

  const extended: Record<string, unknown> = { ...recorded.extended };
  if (answer === "represented" && answers.stand) {
    extended[STAND_IN] = answers.stand;
  } else if (answer !== "represented") {
    // Plus représenté : le remplaçant d'avant ne doit pas rester sur la fiche.
    delete extended[STAND_IN];
  }
  /* ⚠️ `extended` NE PART QUE SI LE REMPLAÇANT CHANGE (bug des réponses UE effacées, chasse
     du 2026-10-20). api-v1 REMPLACE la colonne entière, et d'autres écrans y écrivent (les
     réponses du formulaire d'Informations, par apix) : la renvoyer à chaque écriture, c'est
     écraser ce qu'ils y ont mis depuis notre lecture. Absente du corps, la route laisse la
     colonne telle quelle (`UserEvent.update` de Sequelize n'écrit que les clés reçues). */
  const standMoves =
    JSON.stringify(extended[STAND_IN] ?? null) !==
    JSON.stringify(recorded.extended[STAND_IN] ?? null);

  /**
   * ═══ LES JOURS D'ABORD, LA PARTICIPATION ENSUITE ═══
   *
   * ⚠️ ET L'ORDRE EST TOUT, parce que l'écriture d'un jour a un EFFET DE BORD sur la
   * participation : le serveur enchaîne sur `updateRegisteredPresent`, qui redéfinit
   * `isRegistered` d'après les jours retenus. Écrits après, ils auraient donc le
   * dernier mot sur une colonne que cet appel-ci vient de décider — et l'écran aurait
   * annoncé une inscription que la ligne contredit. Écrits avant, c'est la
   * participation qui conclut, comme partout ailleurs dans ce tunnel.
   *
   * Un effet de bord heureux au passage : c'est ce détour qui pose `registeredAt`,
   * que la V2 n'écrivait nulle part.
   *
   * ⚠️ L'UN APRÈS L'AUTRE, PAS EN PARALLÈLE. La V1 les envoie en `Promise.all` ; chaque
   * appel relit puis réécrit LA MÊME ligne de participation à la fin, et deux en vol
   * se marchent dessus — jusqu'à la branche où le serveur détruit puis recrée cette
   * ligne. Une poignée de jours : la mise en file ne coûte rien de perceptible.
   */
  /**
   * LES JOURS RETENUS, calculés une fois : ils commandent l'écriture des jours ci-dessous
   * ET le filtrage de la billetterie plus bas (un article d'un jour décoché ne doit pas
   * rester facturé). `null` sans étape dates — tous les jours valent, rien à filtrer.
   */
  const selected = daysToWrite(answers, recorded, allDays);

  if (selected) {
    /**
     * ⚠️ LES JOURS RETENUS D'ABORD, LES AUTRES ENSUITE — et ce n'est pas cosmétique.
     * `updateRegisteredPresent` a une branche qui DÉTRUIT puis recrée la ligne de
     * participation, quand le dernier jour retenu tombe. Retirer le lundi avant
     * d'ajouter le mardi la déclenche pour rien, au milieu de l'écriture ; l'inverse
     * ne la déclenche jamais. Il ne reste qu'un cas où elle part, et là c'est
     * justifié : le participant se retire de tout.
     */
    const order = [
      ...allDays.filter((id) => selected.includes(id)),
      ...allDays.filter((id) => !selected.includes(id)),
    ];

    /**
     * ═══ ET SEULEMENT CEUX QUI CHANGENT D'ÉTAT ═══
     *
     * ⚠️ C'EST LA DÉPENSE PRINCIPALE DE CET ÉCRAN, et elle se payait en entier à chaque
     * fois. Ces appels partent EN SÉRIE (voir juste au-dessus, c'est obligatoire), et
     * chacun coûte cher côté serveur : `UserEventDayService.createOrUpdateOne` relit la
     * participation entière — six jointures — écrit le jour, recalcule `isRegistered`,
     * puis relit la participation entière une seconde fois pour la rendre. Un événement
     * de cinq jours payait donc cinq fois cela pour, le plus souvent, réécrire cinq fois
     * la même chose : qui revient corriger son nom ne touche à aucun jour, et qui décline
     * sans avoir jamais été inscrit n'en a aucun à retirer.
     *
     * ⚠️ ON NE SAUTE QUE CE QU'ON SAIT DÉJÀ ÉCRIT. `recorded.days` vient de la route, qui
     * rend `UserEventDays` ; absent, on n'a rien lu, donc on écrit tout, comme avant.
     *
     * ⚠️ ET CELA LAISSE `registeredAt` OÙ IL EST, ce qui est le bon sens même : réécrire
     * un jour inchangé le REDATAIT à chaque enregistrement, si bien que « inscrit le »
     * finissait par dire « dernière fois qu'il a cliqué Enregistrer ».
     */
    const before = recorded.days ? new Set(recorded.days) : null;

    for (const dayId of order) {
      const registered = selected.includes(dayId);
      if (before && before.has(dayId) === registered) continue;
      still();
      await apiFetch(
        `/domains/${domainId}/events/${eventId}/days/${dayId}/users/${userId}`,
        {
          method: "PUT",
          // `registeredAt` remis à `null` quand on se retire : c'est ce que la V1
          // envoie, et une date d'inscription sur un jour non retenu se lirait comme
          // une inscription.
          body: {
            registered: registered,
            registeredAt: registered ? new Date().toISOString() : null,
          },
        },
      );
      progress.days = true;
    }
  }

  /**
   * ═══ LA BILLETTERIE, APRÈS LES JOURS ═══
   *
   * ⚠️ APRÈS EUX, ET CE N'EST PAS UN DÉTAIL D'ORDRE. La route d'écriture ignore un
   * article posé sur un jour que le participant n'a pas retenu
   * (`if (!ued && dayId !== 0) return null`) : envoyée avant, la commande du mardi
   * serait silencieusement jetée parce que le mardi n'est pas encore inscrit. C'est
   * aussi l'ordre de la V1 (`screens/steps/Dates2.jsx`), qui finit par elle.
   *
   * ⚠️ ET SEULEMENT S'IL Y A UNE SÉLECTION À ÉCRIRE. La route commence par DÉTRUIRE ce
   * que la personne avait retenu : appelée à vide sur une inscription qu'on rouvre pour
   * corriger un nom, elle effacerait une commande déjà payée. `undefined` veut dire
   * « l'étape n'a pas été traversée », et n'est donc pas la même chose qu'un objet vide,
   * qui lui veut dire « il n'a rien pris ».
   */
  if (answers.articles) {
    /**
     * ⚠️ NETTOYÉE AVANT L'ÉCRITURE. La route DÉTRUIT la commande avant de la recréer :
     * un article resté sur un jour qu'on a décoché (invisible à l'écran, mais chiffré et
     * facturé) ou un identifiant périmé la ferait échouer ou facturer à tort. Le filtre
     * par jour agit toujours ; celui par catalogue seulement s'il est connu. Voir
     * `nettoieSelection`.
     */
    const own = cleanSelection(answers.articles, {
      ...(selected ? { selectedDays: selected } : {}),
      ...(cached.known ? { known: cached.known } : {}),
    });
    /**
     * ⚠️ ET SEULEMENT SI ELLE CHANGE QUELQUE CHOSE. La route DÉTRUIT avant de recréer :
     * réécrire une commande identique détruit puis recrée des lignes déjà réglées, et
     * fait attendre un aller-retour à l'écran pour un résultat rigoureusement égal. Le
     * cas est le plus courant de tous — traverser la billetterie sans rien y prendre.
     * Commande enregistrée inconnue, on écrit : voir `sameSelection`.
     */
    if (!sameSelection(own, cached.order)) {
      still();
      await saveSelection(domainId, eventId, userId, own);
      progress.tickets = true;
    }
  }

  /**
   * ═══ CE FRONT N'ÉCRIT PAS `isCompleted`, ET C'EST UNE DÉCISION ═══
   *
   * ⚠️ NE PAS LE REBRANCHER SANS RELIRE CE QUI SUIT. La V2 l'a écrit un temps — elle
   * calculait le solde après avoir enregistré la commande, et posait « complet » quand
   * il ne restait rien. C'est ce que fait la V1 (`screens/steps/Dates2.jsx`). Retiré à la
   * demande, parce que le champ ne tient pas ce que son nom promet et que le corriger
   * n'est pas l'affaire d'un front :
   *
   *   — il DÉCIDE DU CONTRÔLE D'ACCÈS (`bo/src/pages/scan/index.jsx:18` refuse l'entrée
   *     sur `!isCompleted`, et ne regarde jamais `isRegistered`), donc une colonne posée
   *     par un navigateur décide qui franchit la porte ;
   *   — il est ÉCRIT PAR LES DEUX FRONTS et par `confirmInvoice`, ce qui explique sa
   *     dérive : relevé en base, il est vrai pour 1 238 personnes de Serre-Chevalier 2026
   *     qui n'ont JAMAIS répondu — inscrites au back office — et faux pour d'autres qui
   *     sont en règle.
   *
   * Ne rien écrire laisse la valeur en place, donc ne change RIEN pour l'existant : la
   * V2 cesse simplement d'être une troisième source de vérité sur une colonne qui en a
   * déjà deux de trop. Le sujet se reprendra côté serveur et back office.
   *
   * Effet de bord heureux : une requête bloquante de moins sur le chemin d'écriture —
   * `fetchResume` partait à chaque confirmation, avant même que l'écran puisse conclure.
   */
  still();
  const written = await apiFetch<{ UserEvent?: UserEvent } | null>(
    `/domains/${domainId}/events/${eventId}/users/${userId}`,
    {
      method: "PUT",
      body: {
        responded: true,
        isRegistered: coming,
        registeredFrom: registrationChannel(),
        // `null` and not `false` when they are not coming: that is what the product
        // writes, and "false" would read as "attends, but on site".
        isRemote: answer === "remote" ? true : coming ? false : null,
        /* Même précaution que pour la réponse elle-même : quelqu'un qui a décliné en
           donnant un motif, et qui revient sans repasser par l'étape 1, ne doit pas voir
           son motif effacé par une réécriture qui ne le portait pas. */
        message:
          answer === "decline"
            ? (answers.reason ?? recorded.reason ?? null) || null
            : null,
        ...(typeof answers.typoId === "number"
          ? { typoId: answers.typoId }
          : {}),
        ...(standMoves ? { extended } : {}),
      },
    },
  );

  /**
   * ═══ SA NOUVELLE TYPOLOGIE GAGNE SES ACCOMPAGNANTS DÉJÀ INSCRITS ═══ (carte #338)
   *
   * Un accompagnant ne choisit pas sa typologie : il HÉRITE de celle du porteur (voir
   * `companionThread.ts`), qui la lui recopie quand il l'inscrit. Mais un porteur qui
   * change ensuite la sienne laissait les siens sur l'ancienne : un exposant devenu
   * visiteur gardait des accompagnants « exposants », avec leurs gabarits d'e-mails et
   * leurs pages.
   *
   *   PUT /domains/:d/events/:e/users/:porteur   api-v1   { typoId, propagate: true }
   *
   * ⚠️ LA CONSIGNE DU BACK OFFICE, À L'IDENTIQUE. `propagate` est lu par le contrôleur
   * d'api-v1, qui réécrit le MÊME corps chez chacun des accompagnants
   * (`registeredBy2` = le porteur), puis le retire : ce n'est pas une colonne. Le bouton
   * de typologie du BO l'envoie déjà ainsi (`bo/src/pages/user/Event/Typologie.jsx`).
   *
   * ⚠️ UN APPEL À PART, ET SON CORPS NE PORTE QUE `typoId`. Le contrôleur recopie le
   * corps ENTIER : joint à l'écriture ci-dessus, il aurait aussi posé chez chacun la
   * réponse du porteur, son `isRegistered`, son motif de refus et son `extended`. Pour le
   * porteur, c'est une réécriture à l'identique : la règle du panier compare les
   * typologies avant toute chose et ne fait rien quand elles sont égales.
   *
   * ⚠️ SEULEMENT QUAND ELLE CHANGE. Une typologie que l'organisateur aurait donnée à la
   * main à un accompagnant (le BO le permet) ne doit pas être écrasée chaque fois que le
   * porteur repasse par la confirmation pour corriger son nom.
   *
   * ⚠️ UN ÉCHEC ICI NE FAIT PAS ÉCHOUER L'INSCRIPTION. Celle du porteur est écrite :
   * remonter l'erreur ferait annoncer « rien n'a été transmis », ce qui serait faux. Une
   * session changée entre-temps (autre onglet) l'arrête de même, sans rien envoyer.
   */
  if (typeof answers.typoId === "number" && answers.typoId !== recorded.typoId) {
    try {
      still();
      await apiFetch(`/domains/${domainId}/events/${eventId}/users/${userId}`, {
        method: "PUT",
        body: { typoId: answers.typoId, propagate: true },
      });
    } catch {
      /* Voir ci-dessus : l'inscription du porteur est faite, elle est rendue. */
    }
  }

  /**
   * ═══ ET ELLE REND L'ENREGISTRÉ, PLUTÔT QUE DE LE FAIRE RELIRE ═══
   *
   * ⚠️ UN ALLER-RETOUR DE MOINS SUR CHAQUE ENREGISTREMENT, sans rien perdre : cette
   * route répond EXACTEMENT ce que la lecture répond — les deux terminent sur
   * `UserEventService.getByUserId`, jours et code compris (vérifié dans
   * `api-v1/api/controllers/UserEvent.controller.js`). La confirmation relisait donc,
   * derrière son « Enregistrement… », une réponse qu'elle tenait déjà.
   *
   * ⚠️ ET C'EST PLUS SÛR QU'UNE RELECTURE, pas seulement plus rapide : ce qui revient ici
   * est l'état d'APRÈS cette écriture-ci, quand une relecture séparée peut attraper celle
   * d'à côté — un autre onglet, un accompagnant en cours d'ajout.
   *
   * ⚠️ RIEN À RENDRE = RIEN DE PROMIS. Une réponse sans participation ne se lirait
   * « pas encore répondu » qu'à tort, juste après avoir écrit le contraire : on rend
   * alors `undefined`, et l'appelant relit (voir `useParticipation`).
   */
  return written?.UserEvent ? recordedOf(written.UserEvent) : undefined;
}

/**
 * ═══ LA LIGNE DE PARTICIPATION CHANGE-T-ELLE ? (journal, chasse du 2026-10-20) ═══
 *
 * `PUT …/users/<id>` part à chaque écriture, changement ou non : c'est elle qui pose
 * `responded`. Ce qu'elle écrit se compare donc à ce que le serveur détenait, lu AVANT
 * (`recorded`) : la réponse (et donc `isRegistered` / `isRemote`), le motif d'un refus, la
 * typologie, le remplaçant. Les autres clés d'`extended` ne repartent pas : la colonne
 * n'est envoyée que si le remplaçant change.
 */
export function rowChanges(answers: Answers, recorded: Recorded): boolean {
  if (!recorded.responded) return true;
  const answer = answers.participation ?? recorded.answer;
  if (answer !== recorded.answer) return true;
  const message =
    answer === "decline"
      ? (answers.reason ?? recorded.reason ?? null) || null
      : null;
  if (message !== (recorded.reason || null)) return true;
  if (typeof answers.typoId === "number" && answers.typoId !== recorded.typoId)
    return true;
  const standBefore = recorded.extended[STAND_IN];
  const standAfter =
    answer === "represented"
      ? (answers.stand ?? standBefore)
      : undefined;
  return JSON.stringify(standAfter ?? null) !== JSON.stringify(standBefore ?? null);
}

/**
 * ═══ CETTE ÉCRITURE A-T-ELLE CHANGÉ QUELQUE CHOSE ? ═══
 *
 * ⚠️ RÉPONDU PAR CE QUI EST PARTI, PAS PAR CE QUE L'ONGLET SE RAPPELLE. L'écriture ne
 * touche un jour que s'il change d'état, et la billetterie que si la commande diffère de
 * l'enregistrée (`progress`) ; la ligne de participation se compare à l'enregistré
 * (`rowChanges`). Un passage complet du tunnel sans rien changer, dans un onglet neuf ou après
 * un rechargement, ne change donc rien, et rien ne se journalise.
 */
export function participationChanged(
  answers: Answers,
  recorded: Recorded,
  progress: Readonly<WriteProgress>,
): boolean {
  return progress.days || progress.tickets || rowChanges(answers, recorded);
}

/**
 * Ce qui a DÉJÀ été écrit quand une écriture échoue en route (bug « rien n'a été transmis »,
 * chasse du 2026-10-20).
 */
export type WriteProgress = { days: boolean; tickets: boolean };

/**
 * ═══ UNE ÉCRITURE EN TROIS TEMPS PEUT S'ARRÊTER AU MILIEU ═══
 *
 * ⚠️ LES JOURS, PUIS LA BILLETTERIE, PUIS LA PARTICIPATION : trois appels, pas une
 * transaction. Si le dernier échoue, les deux premiers sont chez l'organisateur — et l'écran
 * annonçait « Rien n'a été transmis à l'organisateur », ce qui est faux : il a vos jours et
 * vos billets, pas votre réponse. Mesuré : jours en 200, puis `PUT users/<id>` en 500.
 *
 * Cette erreur dit CE QUI EST PARTI, et elle n'est levée que s'il est parti quelque chose :
 * sinon l'erreur d'origine remonte telle quelle, et « rien » reste vrai.
 */
export class PartialWriteError extends Error {
  constructor(
    readonly sent: Readonly<WriteProgress>,
    readonly reason: unknown,
  ) {
    super("partial-write");
    this.name = "PartialWriteError";
  }
}

export async function saveParticipation(
  domainId: number,
  eventId: number,
  userId: number,
  answers: Answers,
  recorded: Recorded,
  allDays: ReadonlyArray<number> = [],
  cached: Parameters<typeof writeParticipation>[6] = {},
  /**
   * Ce qui part, tenu à jour pendant l'écriture. Donné par l'appelant, il lui dit après coup
   * si des jours ou la billetterie ont réellement changé : voir `participationChanged`.
   */
  progress: WriteProgress = { days: false, tickets: false },
): Promise<Recorded | undefined> {
  try {
    return await writeParticipation(
      domainId,
      eventId,
      userId,
      answers,
      recorded,
      allDays,
      cached,
      progress,
    );
  } catch (cause) {
    // Une session changée n'est pas un échec du serveur : elle remonte telle quelle.
    if (cause instanceof SessionChangedError) throw cause;
    if (progress.days || progress.tickets)
      throw new PartialWriteError({ ...progress }, cause);
    throw cause;
  }
}
